/**
 * R8A OTK option catalogs — REMOTE_NON_PROGRESSIVE helpers.
 * Strategy: one full GET on mount; local filter/sort/search; client print/xlsx.
 */
import {
  buildAuditTabulatorColumns,
  mergeColumnVisibilityWithAuditDefaults,
  resolveAuditUser,
  TABULATOR_AUDIT_PROFILES,
  parseAuditDateTime,
} from './tabulatorAuditColumns';
import { inputOnEnterHeaderFilter } from './inputOnEnterHeaderFilter';
import { tabulatorColumnMinWidthFromTitle } from './tabulatorColumnMinWidth';
import { formatDate } from './dateUtils';

export const OTK_OPTION_CATALOG_STRATEGY = 'REMOTE_NON_PROGRESSIVE';

export const OTK_OPTION_CATALOG_REFRESH_EVENT = 'otk-option-catalog-refresh';

export const OTK_OPTION_CATALOG_REFRESH_ACTIONS = Object.freeze([
  'created',
  'updated',
  'deleted',
  'reordered',
]);

/**
 * Typed same-client refresh contract for R8A-3 form/mutation wiring.
 * List listens and performs exactly one full collection GET.
 * @param {{ resource: string, action: string, id?: number|string|null }} payload
 */
export function emitOtkOptionCatalogRefresh(payload) {
  if (typeof window === 'undefined' || !window.dispatchEvent) return false;
  if (!isOtkOptionCatalogRefreshPayload(payload)) return false;
  window.dispatchEvent(
    new CustomEvent(OTK_OPTION_CATALOG_REFRESH_EVENT, { detail: { ...payload } })
  );
  return true;
}

export function isOtkOptionCatalogRefreshPayload(detail, expectedResource) {
  if (!detail || typeof detail !== 'object') return false;
  if (typeof detail.resource !== 'string' || !detail.resource) return false;
  if (!OTK_OPTION_CATALOG_REFRESH_ACTIONS.includes(detail.action)) return false;
  if (expectedResource != null && detail.resource !== expectedResource) return false;
  return true;
}

export function localizedJsonText(value, locale) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    return value?.[locale] || value?.sk || value?.en || value?.de || '';
  }
  return String(value);
}

export function resolveAuditActorDisplayName(user) {
  if (!user) return '';
  return user?.employee?.name || user?.username || '';
}

export function formatAuditDateOnly(value, dateFormat) {
  if (value == null || value === '') return '';
  return formatDate(value, dateFormat || 'dd.MM.yyyy', true);
}

function textIncludesFilter(headerValue, rowValue) {
  if (headerValue == null || String(headerValue).trim() === '') return true;
  const needle = String(headerValue)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  const hay = String(rowValue ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  return hay.includes(needle);
}

function resolveLocale(localeOrGetter) {
  if (typeof localeOrGetter === 'function') {
    try { return localeOrGetter() || 'sk'; } catch (_) { return 'sk'; }
  }
  return localeOrGetter || 'sk';
}

function localizedHeaderFilter(localeOrGetter) {
  return (headerValue, rowValue) => {
    if (headerValue == null || String(headerValue).trim() === '') return true;
    return textIncludesFilter(headerValue, localizedJsonText(rowValue, resolveLocale(localeOrGetter)));
  };
}

function localizedSorter(localeOrGetter) {
  return (a, b) => {
    const locale = resolveLocale(localeOrGetter);
    const valA = localizedJsonText(a, locale);
    const valB = localizedJsonText(b, locale);
    return valA.localeCompare(valB, undefined, { sensitivity: 'base' });
  };
}

export function buildOtkOptionCatalogSearchHaystack(row, { locale, domainKind }) {
  if (!row) return '';
  const parts = [row.id];
  if (domainKind === 'measure_complexity') {
    parts.push(row.category, localizedJsonText(row.description, locale), row.time_in_minutes);
  } else {
    parts.push(row.defined_id, localizedJsonText(row.name, locale));
  }
  const createdUser = resolveAuditUser(row, TABULATOR_AUDIT_PROFILES.createdByUpdatedBy, 'created');
  const updatedUser = resolveAuditUser(row, TABULATOR_AUDIT_PROFILES.createdByUpdatedBy, 'updated');
  parts.push(
    resolveAuditActorDisplayName(createdUser),
    resolveAuditActorDisplayName(updatedUser),
    formatAuditDateOnly(row.created_at),
    formatAuditDateOnly(row.updated_at)
  );
  return parts.filter((p) => p != null && p !== '').join(' ');
}

/** Local toolbar search over the complete in-memory collection. */
export function otkOptionCatalogSearchFilter(data, filterParams) {
  const raw = filterParams?.value;
  if (raw == null || String(raw).trim() === '') return true;
  let regex;
  try {
    regex = new RegExp(String(raw), 'i');
  } catch {
    const escaped = String(raw).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    regex = new RegExp(escaped, 'i');
  }
  const hay = buildOtkOptionCatalogSearchHaystack(data, {
    locale: filterParams?.locale || 'sk',
    domainKind: filterParams?.domainKind || 'name_family',
  });
  return regex.test(hay);
}

/**
 * Whether a catalog exposes Standard 1.1 audit-summary list columns
 * (Založenie / Posledná aktivita) and SetMask toggles for them.
 * Default true for OTK option catalogs (including ErrorType qlty016).
 * Opt out only via includeAuditSummaryColumns: false when a mask must not show audit.
 */
export function catalogIncludesAuditSummaryColumns(config) {
  return config?.includeAuditSummaryColumns !== false;
}

/**
 * Column assembly layout for OTK Settings option catalogs (OTKS1).
 *
 * Family contract (all catalogs):
 *   [row_move?] | selection | editor | ID | BUSINESS… | [AUDIT…]
 *
 * Legacy aliases kept for config/tests:
 * - actions_first — historical qlty016 name for this family layout
 * - standard — deprecated; ignored (all catalogs use family layout)
 */
export const OTK_OPTION_CATALOG_COLUMN_LAYOUTS = Object.freeze({
  standard: 'standard',
  actions_first: 'actions_first',
  family: 'family',
});

/** Always family layout — OTKS1 unified action→ID→business→audit contract. */
export function catalogColumnLayout(_config) {
  return OTK_OPTION_CATALOG_COLUMN_LAYOUTS.family;
}

/** @deprecated use catalogColumnLayout; kept as true for OTKS1 family consumers. */
export function catalogUsesActionsFirstLayout(config) {
  return catalogColumnLayout(config) === OTK_OPTION_CATALOG_COLUMN_LAYOUTS.family;
}

/** Frozen leading fields for a catalog (used by order restoration). */
export function frozenLeadingFieldsForCatalog(config) {
  return config?.reorder
    ? Object.freeze(['row_move', 'rowSelection', 'editor', 'id'])
    : Object.freeze(['rowSelection', 'editor', 'id']);
}

/** Structural fields always forced visible; never offered in SetMask. */
export function requiredVisibilityFieldsForCatalog(_config) {
  return Object.freeze(['id']);
}

/** User-togglable data columns in Nastaviť masku (no structural blanks). */
export function maskConfigurableFieldsForCatalog(config) {
  const withAudit = catalogIncludesAuditSummaryColumns(config);
  if (config.domainKind === 'measure_complexity') {
    return Object.freeze(
      withAudit
        ? ['category', 'description', 'time_in_minutes', 'created_at', 'updated_at']
        : ['category', 'description', 'time_in_minutes']
    );
  }
  return Object.freeze(
    withAudit ? ['defined_id', 'name', 'created_at', 'updated_at'] : ['defined_id', 'name']
  );
}

export function defaultVisibilityForCatalog(config) {
  const next = {};
  for (const field of requiredVisibilityFieldsForCatalog(config)) {
    next[field] = true;
  }
  for (const field of maskConfigurableFieldsForCatalog(config)) {
    next[field] = true;
  }
  return next;
}

export function mergeOtkOptionCatalogVisibility(config, saved) {
  const defaults = defaultVisibilityForCatalog(config);
  const merged = mergeColumnVisibilityWithAuditDefaults(defaults, saved);
  // Structural identity/order columns cannot be hidden via mask.
  for (const field of requiredVisibilityFieldsForCatalog(config)) {
    merged[field] = true;
  }
  return merged;
}

export const OTK_OPTION_CATALOG_COLUMN_I18N_KEYS = Object.freeze({
  id: 'OtkOptionCatalog.Column.Id',
  defined_id: 'OtkOptionCatalog.Column.DefinedId',
  name: 'OtkOptionCatalog.Column.Name',
  category: 'OtkOptionCatalog.Column.Category',
  description: 'OtkOptionCatalog.Column.Description',
  time_in_minutes: 'OtkOptionCatalog.Column.TimeInMinutes',
  created_at: 'OtkOptionCatalog.Column.CreatedAt',
  updated_at: 'OtkOptionCatalog.Column.UpdatedAt',
});

export function getOtkOptionCatalogMaskFieldLabel(field, t) {
  const key = OTK_OPTION_CATALOG_COLUMN_I18N_KEYS[field];
  return key ? t(key) : '';
}

/** Count empty/blank labels among SetMask configurable fields. */
export function countOtkOptionCatalogMaskEmptyLabels(config, t) {
  return maskConfigurableFieldsForCatalog(config).filter((field) => {
    const label = getOtkOptionCatalogMaskFieldLabel(field, t);
    return label == null || String(label).trim() === '';
  }).length;
}

function buildXlsxAuditUserColumns(t, dateFormat) {
  const createdByTitle = t('OtkOptionCatalog.Column.CreatedBy');
  const updatedByTitle = t('OtkOptionCatalog.Column.UpdatedBy');
  return [
    {
      title: createdByTitle,
      field: 'created_by_user_xlsx',
      visible: false,
      print: false,
      download: true,
      accessorDownload: (_v, data) =>
        resolveAuditActorDisplayName(
          resolveAuditUser(data, TABULATOR_AUDIT_PROFILES.createdByUpdatedBy, 'created')
        ),
    },
    {
      title: updatedByTitle,
      field: 'updated_by_user_xlsx',
      visible: false,
      print: false,
      download: true,
      accessorDownload: (_v, data) =>
        resolveAuditActorDisplayName(
          resolveAuditUser(data, TABULATOR_AUDIT_PROFILES.createdByUpdatedBy, 'updated')
        ),
    },
    // ensure datetime cells stay separate from user cells in XLSX
    {
      field: '__audit_date_download_shim__',
      visible: false,
      print: false,
      download: false,
      accessorDownload: () => formatAuditDateOnly(null, dateFormat),
    },
  ];
}

function enhanceAuditColumnsForExport(auditCols, dateFormat) {
  return (auditCols || []).map((col) => {
    if (col.field !== 'created_at' && col.field !== 'updated_at') return col;
    return {
      ...col,
      accessorDownload: (_v, data) => formatAuditDateOnly(data?.[col.field], dateFormat),
      accessorPrint: (_v, data) => {
        const user = resolveAuditUser(
          data,
          TABULATOR_AUDIT_PROFILES.createdByUpdatedBy,
          col.field === 'created_at' ? 'created' : 'updated'
        );
        const name = resolveAuditActorDisplayName(user);
        const dt = formatAuditDateOnly(data?.[col.field], dateFormat);
        return name ? `${name} ${dt}`.trim() : dt;
      },
    };
  });
}

/**
 * Build Tabulator columns for an OTK Settings option catalog list (OTKS1).
 * Family contract:
 *   [row_move?] | selection | editor | ID  (frozen, blank action titles)
 *   then BUSINESS… then AUDIT… (when enabled)
 */
export function buildOtkOptionCatalogColumns({
  config,
  t,
  locale,
  getLocale,
  dateFormat,
  dateTimeFormat,
  getUserColor,
  getUserInitials,
  editColumnFormatter,
}) {
  const localeOrGetter = typeof getLocale === 'function' ? getLocale : locale;
  const includeAuditSummary = catalogIncludesAuditSummaryColumns(config);
  const titleId = t('OtkOptionCatalog.Column.Id');
  const titleDefinedId = t('OtkOptionCatalog.Column.DefinedId');
  const titleName = t('OtkOptionCatalog.Column.Name');
  const titleCategory = t('OtkOptionCatalog.Column.Category');
  const titleDescription = t('OtkOptionCatalog.Column.Description');
  const titleTime = t('OtkOptionCatalog.Column.TimeInMinutes');
  const titleCreated = includeAuditSummary ? t('OtkOptionCatalog.Column.CreatedAt') : '';
  const titleUpdated = includeAuditSummary ? t('OtkOptionCatalog.Column.UpdatedAt') : '';
  const titleEdit = t('Common.Edit');
  const titleMove = t('OtkOptionCatalog.Column.Move');
  const titleSelect = t('OtkOptionCatalog.Column.Select');

  const auditBundle = buildAuditTabulatorColumns({
    profile: TABULATOR_AUDIT_PROFILES.createdByUpdatedBy,
    includeId: true,
    dateFormat,
    dateTimeFormat,
    getUserColor,
    getUserInitials,
    textHeaderFilter: textIncludesFilter,
    idColumnOptions: {
      frozen: true,
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterFunc: textIncludesFilter,
      extra: { title: titleId, print: true, download: true },
    },
  });

  const idCol = {
    ...auditBundle[0],
    title: titleId,
    frozen: true,
    print: true,
    download: true,
  };

  // Action columns: no visible text header; tooltips preserve accessibility.
  const editCol = {
    field: 'editor',
    title: '',
    headerTooltip: titleEdit,
    frozen: true,
    hozAlign: 'center',
    width: 44,
    minWidth: 44,
    headerSort: false,
    headerFilter: false,
    formatter: editColumnFormatter,
    resizable: false,
    print: false,
    download: false,
  };

  // Profile-gated: qlty016 must not promote backend audit fields into list columns.
  const auditCols = includeAuditSummary
    ? enhanceAuditColumnsForExport(
        auditBundle.slice(1).map((col) => ({
          ...col,
          title: col.field === 'created_at' ? titleCreated : titleUpdated,
          frozen: false,
          minWidth: tabulatorColumnMinWidthFromTitle(
            col.field === 'created_at' ? titleCreated : titleUpdated
          ),
        })),
        dateFormat
      )
    : [];

  const selectionCol = {
    field: 'rowSelection',
    title: '',
    headerTooltip: titleSelect,
    titleFormatter: 'rowSelection',
    formatter: 'rowSelection',
    hozAlign: 'center',
    width: 44,
    minWidth: 44,
    headerSort: false,
    headerFilter: false,
    resizable: false,
    frozen: true,
    print: false,
    download: false,
  };

  const moveCol = config.reorder
    ? {
        field: 'row_move',
        title: '',
        headerTooltip: titleMove,
        rowHandle: true,
        formatter: 'handle',
        headerSort: false,
        headerFilter: false,
        width: 44,
        minWidth: 44,
        hozAlign: 'center',
        resizable: false,
        frozen: true,
        print: false,
        download: false,
      }
    : null;

  let domainRest;
  let definedIdCol = null;
  if (config.domainKind === 'measure_complexity') {
    domainRest = [
      {
        title: titleCategory,
        field: 'category',
        frozen: false,
        minWidth: tabulatorColumnMinWidthFromTitle(titleCategory),
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textIncludesFilter,
        print: true,
        download: true,
      },
      {
        title: titleDescription,
        field: 'description',
        frozen: false,
        minWidth: tabulatorColumnMinWidthFromTitle(titleDescription),
        formatter: (cell) => localizedJsonText(cell.getValue(), resolveLocale(localeOrGetter)),
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: localizedHeaderFilter(localeOrGetter),
        sorter: localizedSorter(localeOrGetter),
        accessorDownload: (_v, data) => localizedJsonText(data?.description, resolveLocale(localeOrGetter)),
        accessorPrint: (_v, data) => localizedJsonText(data?.description, resolveLocale(localeOrGetter)),
        print: true,
        download: true,
      },
      {
        title: titleTime,
        field: 'time_in_minutes',
        frozen: false,
        minWidth: tabulatorColumnMinWidthFromTitle(titleTime),
        sorter: 'number',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textIncludesFilter,
        print: true,
        download: true,
      },
    ];
  } else {
    definedIdCol = {
      title: titleDefinedId,
      field: 'defined_id',
      // ID poradia is a normal scrollable business column after the action+ID zone.
      frozen: false,
      minWidth: tabulatorColumnMinWidthFromTitle(titleDefinedId),
      sorter: 'number',
      sorterParams: { alignEmptyValues: 'bottom' },
      headerFilter: inputOnEnterHeaderFilter,
      headerFilterLiveFilter: false,
      headerFilterFunc: textIncludesFilter,
      print: true,
      download: true,
    };
    domainRest = [
      {
        title: titleName,
        field: 'name',
        frozen: false,
        minWidth: tabulatorColumnMinWidthFromTitle(titleName),
        formatter: (cell) => localizedJsonText(cell.getValue(), resolveLocale(localeOrGetter)),
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: localizedHeaderFilter(localeOrGetter),
        sorter: localizedSorter(localeOrGetter),
        accessorDownload: (_v, data) => localizedJsonText(data?.name, resolveLocale(localeOrGetter)),
        accessorPrint: (_v, data) => localizedJsonText(data?.name, resolveLocale(localeOrGetter)),
        print: true,
        download: true,
      },
    ];
  }

  const xlsxUserCols = includeAuditSummary
    ? buildXlsxAuditUserColumns(t, dateFormat).filter(
        (c) => c.field !== '__audit_date_download_shim__'
      )
    : [];

  // OTKS1: drag | selection | edit | ID (frozen) → business → audit
  const frozenLeading = [];
  if (moveCol) frozenLeading.push(moveCol);
  frozenLeading.push(selectionCol, editCol, idCol);
  const afterFrozen = [];
  if (definedIdCol) afterFrozen.push(definedIdCol);
  afterFrozen.push(...domainRest);
  return [...frozenLeading, ...afterFrozen, ...auditCols, ...xlsxUserCols];
}

export function assertOtkOptionCatalogColumnContract(columns, config) {
  const cols = columns || [];
  const visible = cols.filter((c) => c.visible !== false);
  const idIndex = cols.findIndex((c) => c.field === 'id');
  const editIndex = cols.findIndex((c) => c.field === 'editor');
  const definedIndex = cols.findIndex((c) => c.field === 'defined_id');
  const selectionIndex = cols.findIndex((c) => c.field === 'rowSelection');
  const moveIndex = cols.findIndex((c) => c.rowHandle === true || c.field === 'row_move');
  const createdIndex = cols.findIndex((c) => c.field === 'created_at');
  const updatedIndex = cols.findIndex((c) => c.field === 'updated_at');
  const hasReorderHandle = moveIndex >= 0;
  const includeAuditSummary = catalogIncludesAuditSummaryColumns(config);
  const frozenLeading = frozenLeadingFieldsForCatalog(config);
  const actionFields = new Set(['row_move', 'rowSelection', 'editor']);

  const actionCols = visible.filter((c) => actionFields.has(c.field));
  const actionTitlesBlank = actionCols.every((c) => !c.title || String(c.title).trim() === '');
  const selectionKeepsSelectAll = cols[selectionIndex]?.titleFormatter === 'rowSelection';

  const frozenPrefixOk = frozenLeading.every(
    (field, index) => visible[index]?.field === field && visible[index]?.frozen === true
  );

  const firstBusinessField =
    config.domainKind === 'measure_complexity' ? 'category' : 'defined_id';
  const businessStart = frozenLeading.length;
  const leadingOk =
    frozenPrefixOk &&
    visible[businessStart]?.field === firstBusinessField &&
    visible[businessStart]?.frozen !== true &&
    (config.domainKind === 'measure_complexity'
      ? visible[businessStart + 1]?.field === 'description'
      : visible[businessStart + 1]?.field === 'name' &&
        visible[businessStart + 1]?.frozen !== true);

  const allActionsBeforeId =
    idIndex >= 0 &&
    actionCols.every((c) => cols.findIndex((x) => x.field === c.field) < idIndex);

  return {
    idPresent: idIndex >= 0,
    // OTKS1: ID is first identity column after the action zone (not absolute first).
    idFirstVisible: visible[frozenLeading.indexOf('id')]?.field === 'id',
    idFrozen: cols[idIndex]?.frozen === true,
    editBeforeId: editIndex >= 0 && editIndex < idIndex,
    editFrozen: cols[editIndex]?.frozen === true,
    // Action columns must NOT show textual headers.
    editTitled: !!(cols[editIndex]?.title && String(cols[editIndex].title).trim()),
    actionTitlesBlank,
    selectionKeepsSelectAll,
    definedIdAfterIdZone:
      config.domainKind === 'measure_complexity' ||
      (definedIndex === frozenLeading.length && cols[definedIndex]?.frozen !== true),
    // Legacy alias used by older tests — now means selection is inside frozen action zone before ID.
    selectionAfterFrozenBlock:
      selectionIndex >= 0 && selectionIndex < idIndex && cols[selectionIndex]?.frozen === true,
    // Legacy alias — drag is first when reorder is enabled.
    moveAfterFrozenBlock:
      !config.reorder ||
      (hasReorderHandle && moveIndex === 0 && cols[moveIndex]?.frozen === true),
    // Count of intentionally blank action titles before ID (OTKS1 expects these blanks).
    blankTitlesBeforeId: actionCols.filter((c) => !c.title || String(c.title).trim() === '').length,
    leadingOrderContract: leadingOk && allActionsBeforeId && actionTitlesBlank && selectionKeepsSelectAll,
    frozenLeadingFields: frozenLeading,
    actionsFirstLayout: true,
    auditAtEndOfDomain: includeAuditSummary
      ? createdIndex > idIndex && updatedIndex === createdIndex + 1
      : createdIndex < 0 && updatedIndex < 0,
    includeAuditSummaryColumns: includeAuditSummary,
    hasReorderHandle,
    reorderMatchesConfig: config.reorder === hasReorderHandle,
    noDefinedIdForMeasureComplexity:
      config.domainKind !== 'measure_complexity' || definedIndex < 0,
    dataFields: cols
      .filter((c) => c.field && !['rowSelection', 'editor', 'row_move'].includes(c.field))
      .filter((c) => c.visible !== false || c.field === 'id')
      .map((c) => c.field),
  };
}

/** Config for each R8A list mask. */
export const OTK_OPTION_CATALOG_CONFIGS = Object.freeze({
  groups: Object.freeze({
    resource: 'groups',
    mask: 'qlty018',
    pageKey: 'GroupList',
    getUrl: '/api/groups',
    deleteUrl: (id) => `/api/groups/${id}`,
    reorderUrl: '/api/groups/reorder',
    reorderFormKey: 'groups',
    stateKey: 'groups',
    loadAction: 'load_groups',
    loadPayloadKey: 'newGroups',
    domainKind: 'name_family',
    reorder: true,
    addPage: 'GroupAdd',
    addMask: 'qlty019',
    entityProp: 'group',
    favoriteTitleKey: 'GroupList.FavoriteTitle',
    printTitleKey: 'GroupList.PrintTitle',
    xlsxFilename: 'groups.xlsx',
    tabAddKey: 'GroupList.Tab.Add',
    tabEditKey: 'GroupList.Tab.Edit',
  }),
  preventions: Object.freeze({
    resource: 'preventions',
    mask: 'qlty020',
    pageKey: 'PreventionList',
    getUrl: '/api/preventions',
    deleteUrl: (id) => `/api/preventions/${id}`,
    reorderUrl: '/api/preventions/reorder',
    reorderFormKey: 'preventions',
    stateKey: 'preventions',
    loadAction: 'load_preventions',
    loadPayloadKey: 'newPreventions',
    domainKind: 'name_family',
    reorder: true,
    addPage: 'PreventionAdd',
    addMask: 'qlty021',
    entityProp: 'prevention',
    favoriteTitleKey: 'PreventionList.FavoriteTitle',
    printTitleKey: 'PreventionList.PrintTitle',
    xlsxFilename: 'preventions.xlsx',
    tabAddKey: 'PreventionList.Tab.Add',
    tabEditKey: 'PreventionList.Tab.Edit',
  }),
  measure_complexities: Object.freeze({
    resource: 'measure_complexities',
    mask: 'qlty022',
    pageKey: 'MeasureComplexityList',
    getUrl: '/api/measure_complexities',
    deleteUrl: (id) => `/api/measure_complexities/${id}`,
    reorderUrl: null,
    reorderFormKey: null,
    stateKey: 'measure_complexities',
    loadAction: 'load_measure_complexities',
    loadPayloadKey: 'newMeasureComplexities',
    domainKind: 'measure_complexity',
    reorder: false,
    addPage: 'MeasureComplexityAdd',
    addMask: 'qlty023',
    entityProp: 'measure_complexity',
    favoriteTitleKey: 'MeasureComplexityList.FavoriteTitle',
    printTitleKey: 'MeasureComplexityList.PrintTitle',
    xlsxFilename: 'measure_complexities.xlsx',
    tabAddKey: 'MeasureComplexityList.Tab.Add',
    tabEditKey: 'MeasureComplexityList.Tab.Edit',
  }),
  root_causes: Object.freeze({
    resource: 'root_causes',
    mask: 'qlty034',
    pageKey: 'RootCauseList',
    getUrl: '/api/root_causes',
    deleteUrl: (id) => `/api/root_causes/${id}`,
    reorderUrl: '/api/root_causes/reorder',
    reorderFormKey: 'root_causes',
    stateKey: 'root_causes',
    loadAction: 'load_root_causes',
    loadPayloadKey: 'newRootCauses',
    domainKind: 'name_family',
    reorder: true,
    addPage: 'RootCauseAdd',
    addMask: 'qlty035',
    entityProp: 'root_cause',
    favoriteTitleKey: 'RootCauseList.FavoriteTitle',
    printTitleKey: 'RootCauseList.PrintTitle',
    xlsxFilename: 'root_causes.xlsx',
    tabAddKey: 'RootCauseList.Tab.Add',
    tabEditKey: 'RootCauseList.Tab.Edit',
  }),
  protocol_types: Object.freeze({
    resource: 'protocol_types',
    mask: 'qlty038',
    pageKey: 'ProtocolTypeList',
    getUrl: '/api/protocol_types',
    deleteUrl: (id) => `/api/protocol_types/${id}`,
    reorderUrl: '/api/protocol_types/reorder',
    reorderFormKey: 'protocol_types',
    stateKey: 'protocol_types',
    loadAction: 'load_protocol_types',
    loadPayloadKey: 'newProtocolTypes',
    domainKind: 'name_family',
    reorder: true,
    addPage: 'ProtocolTypeAdd',
    addMask: 'qlty039',
    entityProp: 'protocol_type',
    favoriteTitleKey: 'ProtocolTypeList.FavoriteTitle',
    printTitleKey: 'ProtocolTypeList.PrintTitle',
    xlsxFilename: 'protocol_types.xlsx',
    tabAddKey: 'ProtocolTypeList.Tab.Add',
    tabEditKey: 'ProtocolTypeList.Tab.Edit',
  }),
  error_types: Object.freeze({
    resource: 'error_types',
    mask: 'qlty016',
    pageKey: 'ErrorTypeList',
    getUrl: '/api/error_types',
    deleteUrl: (id) => `/api/error_types/${id}`,
    reorderUrl: '/api/error_types/reorder',
    reorderFormKey: 'error_types',
    stateKey: 'error_types',
    loadAction: 'load_error_types',
    loadPayloadKey: 'newErrorTypes',
    domainKind: 'name_family',
    reorder: true,
    // OTKS1C: restore Standard 1.1 audit-summary columns (Založenie / Posledná aktivita)
    // after the OTKS1 family action→ID→business layout. Backend audit already exists.
    columnLayout: OTK_OPTION_CATALOG_COLUMN_LAYOUTS.family,
    addPage: 'ErrorTypeAdd',
    addMask: 'qlty017',
    entityProp: 'error_type',
    favoriteTitleKey: 'ErrorTypeList.FavoriteTitle',
    printTitleKey: 'ErrorTypeList.PrintTitle',
    xlsxFilename: 'error_types.xlsx',
    tabAddKey: 'ErrorTypeList.Tab.Add',
    tabEditKey: 'ErrorTypeList.Tab.Edit',
  }),
});

export function getOtkOptionCatalogConfig(resource) {
  return OTK_OPTION_CATALOG_CONFIGS[resource] || null;
}

export function validateFullCollectionResponse(data) {
  return Array.isArray(data) ? data : null;
}

/** Simulate network-count contract for tests (no progressive remote paging). */
export function simulateLocalInteractionNetworkCounts() {
  return {
    FILTER_NETWORK_GET_COUNT: 0,
    SORT_NETWORK_GET_COUNT: 0,
    SEARCH_NETWORK_GET_COUNT: 0,
    HIDDEN_PANEL_GET_COUNT: 0,
    REDRAW_GET_COUNT: 0,
  };
}

/**
 * Classify a deleteRequest JSON body. deleteRequest resolves (does not throw)
 * for typical API error envelopes with body.status >= 400.
 */
export function isOtkOptionCatalogDeleteFailure(body) {
  if (body == null) return true;
  const status = typeof body.status === 'number' ? body.status : null;
  return status != null && status >= 400;
}

/**
 * Batch-delete selected catalog rows.
 * N independent DELETE calls (Promise.allSettled), then exactly ONE catalog GET.
 * Never emits otk-option-catalog-refresh (delete owns direct refresh).
 *
 * Partial failure: still one refresh; never toast full success if any row failed.
 *
 * @returns {Promise<{
 *   deleteCount: number,
 *   catalogGetCount: number,
 *   succeeded: number,
 *   failed: number,
 *   typedEventEmitCount: number,
 * }>}
 */
export async function deleteOtkOptionCatalogSelectedRows({
  rows,
  deleteUrl,
  deleteRequestFn,
  fetchFullCollection,
  toast,
  t,
}) {
  const selected = Array.isArray(rows) ? rows : [];
  if (selected.length === 0) {
    return {
      deleteCount: 0,
      catalogGetCount: 0,
      succeeded: 0,
      failed: 0,
      typedEventEmitCount: 0,
    };
  }

  const results = await Promise.allSettled(
    selected.map((row) =>
      Promise.resolve(deleteRequestFn(deleteUrl(row.id))).then((body) => {
        if (isOtkOptionCatalogDeleteFailure(body)) {
          const err = new Error(body?.message || 'Delete failed');
          err.body = body;
          throw err;
        }
        return body;
      })
    )
  );

  const succeeded = results.filter((r) => r.status === 'fulfilled').length;
  const failed = results.length - succeeded;

  // Exactly one full collection refresh after the batch settles.
  await Promise.resolve(fetchFullCollection());

  if (failed === 0) {
    toast?.current?.show({
      severity: 'info',
      summary: t('ServiceTask.Confirmed'),
      life: 3000,
      detail: t('ServiceTask.RecordDeleted'),
    });
  } else {
    // Full or partial failure — never claim all rows deleted.
    for (const r of results) {
      if (r.status === 'rejected') {
        console.error(r.reason);
      }
    }
    toast?.current?.show({
      severity: 'warn',
      summary: t('ServiceTask.Rejected'),
      detail: t('ServiceTask.DeleteFailed'),
      life: 3000,
    });
  }

  return {
    deleteCount: selected.length,
    catalogGetCount: 1,
    succeeded,
    failed,
    typedEventEmitCount: 0,
  };
}

// Keep parseAuditDateTime import used for null-gate tests.
export { parseAuditDateTime, resolveAuditUser, TABULATOR_AUDIT_PROFILES };
